import Context from "shared/util/Context";
import OkC from "shared/util/OkcApi";
import isApp from "shared/util/isApp";
import _str from "shared/util/_str";
import Cookies from "shared/util/Cookies";
import Facebook from "shared/util/Facebook";
import ClientStats from "shared/util/ClientStats";
import AppPhoneCommand from "shared/util/AppPhoneCommand";
import { STATUS_CODES, SCREENS } from "apps/login_old/util/constants";
import { trackSignInSelectedFacebook } from "analytics/login";


// action types
// ----------------------------------------------------------------------------

export const LOADED = "LOGIN.LOADED";
export const SUBMIT_LOGIN = "LOGIN.SUBMIT_LOGIN";
export const SUBMIT_LOGIN_SUCCESS = "LOGIN.SUBMIT_LOGIN_SUCCESS";
export const SUBMIT_LOGIN_FAIL = "LOGIN.SUBMIT_LOGIN_FAIL";
export const SUBMIT_RESET = "LOGIN.SUBMIT_RESET";
export const SUBMIT_RESET_SUCCESS = "LOGIN.SUBMIT_RESET_SUCCESS";
export const SUBMIT_RESET_FAIL = "LOGIN.SUBMIT_RESET_FAIL";
export const CHANGE_SCREEN = "LOGIN.CHANGE_SCREEN";
export const SHOW_REACTIVATE = "LOGIN.SHOW_REACTIVATE";
export const SHOW_TWO_FACTOR = "LOGIN.SHOW_TWO_FACTOR";
export const SHOW_CAPTCHA = "LOGIN.SHOW_CAPTCHA";
export const CONFIRM_RECAPTCHA_SUCCESS = "LOGIN.CONFIRM_RECAPTCHA_SUCCESS";


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	reactivateAuthcode: null,
	reactivateUserid: null,
	screen: SCREENS.LOGIN,
	isLoading: false,
	isLoggedIn: false,
	isSubmitting: false,
	showFacebookLogin: true,
	showTwoFactor: false,
	error: null,
};

const getInitialState = () => {
	let initialState = {
		...INITIAL_STATE,
	};

	if (typeof window !== "undefined" && window.LoginParams) {
		initialState = {
			...initialState,
			...window.LoginParams,
		};
	}

	return initialState;
};


// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case SUBMIT_LOGIN:
			return {
				...state,
				isLoading: true,
				isSubmitting: true,
			};

		case SUBMIT_LOGIN_SUCCESS:
			return {
				...state,
				isLoading: false,
				isSubmitting: false,
				isLoggedIn: true,
			};

		case SUBMIT_LOGIN_FAIL:
			return {
				...state,
				isLoading: false,
				isSubmitting: false,
				error: action.error,
			};

		case SUBMIT_RESET:
			return {
				...state,
				isLoading: true,
			};

		case SUBMIT_RESET_SUCCESS:
			return {
				...state,
				isLoading: false,
				screen: SCREENS.PASSWORD_SUBMIT,
			};

		case SUBMIT_RESET_FAIL:
			return {
				...state,
				isLoading: false,
				error: action.error,
			};

		case CHANGE_SCREEN:
			return {
				...state,
				screen: action.screen,
			};

		case SHOW_REACTIVATE:
			return {
				...state,
				reactivateAuthcode: action.authcode,
				reactivateUserid: action.userid,
				screen: SCREENS.REACTIVATE,
			};

		case SHOW_TWO_FACTOR:
			return {
				...state,
				showTwoFactor: true,
				isSubmitting: false,
			};

		case SHOW_CAPTCHA:
			return {
				...state,
				isSubmitting: false,
				screen: SCREENS.CAPTCHA,
			};

		default:
			return state;
	}
}


// action creators
// ----------------------------------------------------------------------------

function _goHome({ onboardingIncomplete } = {}) {
	if (isApp()) {
		try {
			AppPhoneCommand("showNativeChrome");
			AppPhoneCommand("cancelAllScheduledNotifications");
			AppPhoneCommand("showNativeChrome");
			AppPhoneCommand("registerForRemoteNotifications");
			window.appStorage.setItem("isUser", true);
		} catch (e) {
			// do nothing and hope I guess?
		}

		// Yo, this needs to stay "quickmatch" because of how the apps determine
		// what views are native! Don't be all like "oh we named this fucker to
		// double whatever I'll change it here" 'cause then Jordan will come and
		// break yer kneecaps and nobody wants that right?
		let landingPage = "/quickmatch";

		if (onboardingIncomplete) {
			landingPage = "/onboarding";
		} else if (window.location.pathname.indexOf("/l/") === 0) {
			landingPage = window.location.href;
		}

		window.App.loadPage(landingPage, {
			post_login: 1,
			clear_history: 1,
		});
	} else if (onboardingIncomplete) {
		window.location.href = "/onboarding";
	} else if (window.location.pathname.indexOf("/l/") === 0) {
		window.location.reload();
	} else if (process.env.PLATFORM === "desktop") {
		window.location.href = "/home";
	} else {
		window.location.href = "/doubletake";
	}
}

export function loadLogin() {
	/**
	  * This sets a cookie on the browser via javascript. This means if we see
	  * a login that doesn't contain this cookie chances are the login was
	  * automated. This is negated by tools that instrument a real browser or
	  * people who catch onto the cookie. However, this still addresses the
	  * low hanging fruit of automated logins.
	  */
	Cookies.setCookie("ua", "531227642bc86f3b5fd7103a0c0b4fd6");

	// load the FB lib
	Facebook.load();
	return { type: LOADED };
}

/**
 * This helper is a generic handler for login error catching; it works for both
 * OkC login and facebook login, and dispatches new actions accordingly.
 *
 * @param {dict} res - the result of the api call
 * @param {func} dispatch - a redux dispatcher
 */
const _handleLoginError = (res, dispatch) => {
	let error = null;

	if (res) {
		const type = res.status;

		if (type === STATUS_CODES.DISABLED) {
			dispatch({
				type: SHOW_REACTIVATE,
				authcode: res.reenable_authcode,
				userid: res.reenable_userid,
			});
			return;
		} else if (type === STATUS_CODES.NEEDS_2FA) {
			dispatch({ type: SHOW_TWO_FACTOR });
			return;
		} else if (type === STATUS_CODES.NEED_CAPTCHA) {
			dispatch({ type: SHOW_CAPTCHA });
			return;
		}

		if (res.status_str) {
			error = {
				type,
				message: res.status_str,
			};
		} else if (res.error) {
			error = res.error;
		}
	}

	if (!error) {
		error = {
			type: "UNKNOWN",
			message: _str("Something went wrong, please try again later"),
		};
	}

	// shake the phone bro
	AppPhoneCommand("vibratePhone");

	dispatch({
		type: SUBMIT_LOGIN_FAIL,
		error,
	});
};

export function reactivateAccount() {
	return (dispatch, getStore) => {
		dispatch({ type: SUBMIT_LOGIN });

		const state = getStore().login;

		// We need both of these
		if (!state.reactivateUserid || !state.reactivateAuthcode) {
			return;
		}

		const params = {
			okc_api: 1,
			reenable_userid: state.reactivateUserid,
			reenable_authcode: state.reactivateAuthcode,
		};

		OkC.legacy("/login", {
			type: "POST",
			data: params,
		})
			.then((res) => {
				if (!res || res.status) {
					_handleLoginError(res, dispatch);
					return;
				}

				ClientStats.update("login - onboarding funnel - login - success");
				dispatch({ type: SUBMIT_LOGIN_SUCCESS });
				_goHome({ onboardingIncomplete: res.onboarding_incomplete });
			})
			.catch((err) => {
				ClientStats.update("login - onboarding funnel - login - failed");
				_handleLoginError(err, dispatch);
			});
	};
}

export function submitLogin(args) {
	return (dispatch) => {
		dispatch({ type: SUBMIT_LOGIN });

		const missingCredentials = !args.username || !args.password;

		if (missingCredentials) {
			dispatch({
				type: SUBMIT_LOGIN_FAIL,
				error: {
					type: "Incomplete Form",
					message: _str(`Uh-oh! Please enter your ${!args.username ? "email" : "password"} to login.`),
				},
			});

			return;
		}

		const params = {
			okc_api: 1,
			username: args.username,
			password: args.password,
		};

		if (Context.get("state").FORCE_CAPTCHA_LOGIN) {
			params.captcha = 1;
		}

		if (args.authcode) {
			params.totp_code = args.authcode;
			params.totp_save_device = args.saveAuth;
		}

		if (args.captchaKey) {
			params.recaptcha_response = args.captchaKey;
		}

		OkC.legacy("/login", {
			type: "POST",
			data: params,
		})
			.then((res) => {
				if (!res || res.status) {
					_handleLoginError(res, dispatch);
					return;
				}

				ClientStats.update("login - onboarding funnel - login - success");
				dispatch({ type: SUBMIT_LOGIN_SUCCESS });

				_goHome({ onboardingIncomplete: res.onboarding_incomplete });
			})
			.catch((err) => {
				ClientStats.update("login - onboarding funnel - login - failed");
				_handleLoginError(err, dispatch);
			});
	};
}

export function facebookLogin() {
	return (dispatch) => {
		dispatch({ type: SUBMIT_LOGIN });

		ClientStats.add("login - onboarding funnel - facebook - clicked");
		trackSignInSelectedFacebook();

		Facebook.login()
			.then((res) => {
				if (!res || res.status) {
					_handleLoginError(res, dispatch);
					return;
				}

				ClientStats.update({
					statname: "login - onboarding funnel - facebook - success",

					cb() {
						dispatch({ type: SUBMIT_LOGIN_SUCCESS });
						_goHome();
					},
				});
			})
			.catch((err) => {
				ClientStats.update("login - onboarding funnel - facebook - failed");
				_handleLoginError(err, dispatch);
			});
	};
}

export function submitReset(email) {
	return (dispatch) => {
		dispatch({
			type: SUBMIT_RESET,
			email,
		});

		OkC.post("/okc/lost_password", {
			data: { email },
		})
			.then((res) => {
				if (res.code) {
					dispatch({
						type: SUBMIT_RESET_FAIL,
						error: {
							type: res.message,
							message: res.display,
						},
					});
				} else {
					dispatch({ type: SUBMIT_RESET_SUCCESS });
				}
			})
			.catch(() => {
				dispatch({
					type: SUBMIT_RESET_FAIL,
					error: {
						type: "UNKNOWN",
						message: "Something went wrong, refresh and try again",
					},
				});
			});
	};
}

export function changeScreen(screen) {
	return {
		type: CHANGE_SCREEN,
		screen,
	};
}



// WEBPACK FOOTER //
// ./src/ducks/login_old.js